import * as THREE from 'three/webgpu';
import {
	Break,
	Fn,
	If,
	Loop,
	cameraFar,
	cameraPosition,
	float,
	frameId,
	hash,
	max,
	min,
	mix,
	modelWorldMatrixInverse,
	mrt,
	positionGeometry,
	pow,
	property,
	screenCoordinate,
	smoothstep,
	varying,
	vec2,
	vec3,
	vec4
} from 'three/tsl';
import { createCloudDensitySampler } from './createCloudDensitySampler.js';

const PI4 = Math.PI * 4;

const hitBox = Fn( ( { origin, direction } ) => {

	const inverseDirection = direction.reciprocal();
	const minimum = min( vec3( - 0.5 ).sub( origin ).mul( inverseDirection ), vec3( 0.5 ).sub( origin ).mul( inverseDirection ) );
	const maximum = max( vec3( - 0.5 ).sub( origin ).mul( inverseDirection ), vec3( 0.5 ).sub( origin ).mul( inverseDirection ) );
	const entry = max( minimum.x, max( minimum.y, minimum.z ) );
	const exit = min( maximum.x, min( maximum.y, maximum.z ) );

	return vec2( entry, exit );

} );

const henyeyGreenstein = Fn( ( [ cosine, anisotropy ] ) => {

	const g2 = anisotropy.mul( anisotropy );
	const denominator = pow(
		float( 1 ).add( g2 ).sub( anisotropy.mul( cosine ).mul( 2 ) ),
		1.5
	).mul( PI4 );

	return float( 1 ).sub( g2 ).div( max( denominator, 0.0001 ) );

} );

export function createCloudMaterial( baseTexture, detailTexture, uniforms ) {

	const cloudDataOutput = property( 'vec4', 'cyborgCloudData' );
	const sampleDensity = createCloudDensitySampler( baseTexture, detailTexture, uniforms );

	const fragmentNode = Fn( () => {

		cloudDataOutput.assign( vec4( cameraFar, cameraFar, 0, 0 ) );

		const radiance = vec3( 0 ).toVar();
		const transmittance = float( 1 ).toVar();
		const occupancy = float( 0 ).toVar();
		const lightAccumulator = float( 0 ).toVar();
		const meanDistance = float( 0 ).toVar();
		const depthWeight = float( 0 ).toVar();
		const frontDistance = float( 0 ).toVar();
		const medianDistance = float( 0 ).toVar();
		const frontFound = float( 0 ).toVar();
		const medianFound = float( 0 ).toVar();
		const cameraLocal = vec3( modelWorldMatrixInverse.mul( vec4( cameraPosition, 1 ) ) );
		const rayDirectionLocal = varying( positionGeometry.sub( cameraLocal ) ).normalize();
		const rayDirectionWorld = rayDirectionLocal.mul( uniforms.volumeSize ).normalize();
		const bounds = vec2( hitBox( { origin: cameraLocal, direction: rayDirectionLocal } ) ).toVar();

		bounds.x.greaterThan( bounds.y ).discard();
		const cameraInsideVolume = bounds.x.lessThan( 0 );
		bounds.x.assign( max( bounds.x, 0 ) );

		const worldUnitsPerLocalUnit = rayDirectionLocal.mul( uniforms.volumeSize ).length();
		const raySegmentLocal = bounds.y.sub( bounds.x );
		// Interior views reveal the discrete camera-centered sampling shells much
		// more readily than exterior views. Spend extra intervals inside the volume
		// and bias them toward the camera while retaining a bounded shader loop. The
		// count must not depend on per-ray exit distance: rounding such a count makes
		// adjacent rays repartition their entire march and produces fan-shaped bands.
		const activeViewSteps = cameraInsideVolume.select( float( 220 ), uniforms.viewSteps );
		const intervalCurve = float( 1 );
		const detailVoxelWorld = uniforms.worldScale
			.div( uniforms.detailResolution.mul( uniforms.shapeScale ).mul( uniforms.detailScale ) );
		const phaseCosine = rayDirectionWorld.dot( uniforms.sunDirection ).clamp( - 1, 1 );
		const phase = mix(
			henyeyGreenstein( phaseCosine, uniforms.phaseBackward ),
			henyeyGreenstein( phaseCosine, uniforms.phaseForward ),
			uniforms.phaseMix
		).mul( 1.55 );
		const pixelSeed = screenCoordinate.x.mul( 1973 ).add( screenCoordinate.y.mul( 9277 ) );
		const totalLightDistance = uniforms.volumeSize.length().mul( uniforms.lightDistance );
		const lightDirectionLocal = uniforms.sunDirection.div( uniforms.volumeSize );
		const activeLightSteps = cameraInsideVolume.select( float( 12 ), uniforms.lightSteps );

		Loop( 220, ( { i: viewIndex } ) => {

			If( float( viewIndex ).greaterThanEqual( activeViewSteps ), () => {

				Break();

			} );

			const intervalStartUnit = pow( float( viewIndex ).div( activeViewSteps ), intervalCurve );
			const intervalEndUnit = pow( float( viewIndex ).add( 1 ).div( activeViewSteps ), intervalCurve );
			const intervalStartLocal = bounds.x.add( raySegmentLocal.mul( intervalStartUnit ) );
			const intervalEndLocal = bounds.x.add( raySegmentLocal.mul( intervalEndUnit ) );
			const intervalLocal = intervalEndLocal.sub( intervalStartLocal );
			const intervalWorld = intervalLocal.mul( worldUnitsPerLocalUnit );
			const viewSequenceSeed = pixelSeed.add( float( viewIndex ).mul( 26699 ) );
			const stableViewPhase = hash( viewSequenceSeed );
			const animatedViewPhase = hash( viewSequenceSeed.add( frameId.mul( 31847 ) ) );
			const sequencedViewPhase = mix(
				stableViewPhase,
				animatedViewPhase,
				uniforms.animatedJitter.mul( 0.35 )
			);
			const samplePhase = mix( 0.5, sequencedViewPhase, uniforms.jitterStrength );
			const sampleLocal = mix( intervalStartLocal, intervalEndLocal, samplePhase );
			const positionRay = cameraLocal.add( rayDirectionLocal.mul( sampleLocal ) );
			const detailLod = smoothstep(
				detailVoxelWorld.mul( 0.75 ),
				detailVoxelWorld.mul( 3 ),
				intervalWorld
			).oneMinus();

			const densityData = sampleDensity( positionRay, detailLod );
			const density = float( densityData.x ).toVar();

			If( density.greaterThan( 0.002 ), () => {

				const lightOpticalDepth = float( 0 ).toVar();

				Loop( 12, ( { i: lightIndex } ) => {

					If( float( lightIndex ).greaterThanEqual( activeLightSteps ), () => {

						Break();

					} );

					const intervalStart = pow(
						float( lightIndex ).div( activeLightSteps ),
						uniforms.shadowDistribution
					);
					const intervalEnd = pow(
						float( lightIndex ).add( 1 ).div( activeLightSteps ),
						uniforms.shadowDistribution
					);
					const sampleDistance = totalLightDistance.mul( intervalStart.add( intervalEnd ).mul( 0.5 ) );
					const intervalDistance = totalLightDistance.mul( intervalEnd.sub( intervalStart ) );
					const lightPosition = positionRay.add( lightDirectionLocal.mul( sampleDistance ) );
					// Direct-light integration is much more sensitive to high-frequency
					// density than the view integral. Filter only the lighting lookup for
					// interior views so sparse sun samples do not become visible contours.
					const lightDetailLod = cameraInsideVolume.select( min( detailLod, 0.35 ), detailLod );
					lightOpticalDepth.addAssign(
						sampleDensity( lightPosition, lightDetailLod ).x.mul( intervalDistance )
					);

				} );

				const lightTransmittance = lightOpticalDepth.mul( uniforms.extinction ).mul( - 0.82 ).exp();
				const height = densityData.w;
				const ambient = mix( uniforms.ambientBottom, uniforms.ambientTop, height )
					.mul( uniforms.ambientIntensity );
				const powder = float( 1 ).add(
					float( 1 ).sub( density.mul( 2.2 ).negate().exp() ).mul( uniforms.powder )
				);
				const sunlight = uniforms.sunColor.mul( uniforms.sunIntensity )
					.mul( phase ).mul( lightTransmittance ).mul( powder );
				const lighting = ambient.add( sunlight ).mul( uniforms.cloudColor );
				const stepTransmittance = density.mul( uniforms.extinction ).mul( intervalWorld ).negate().exp();
				const scatteredFraction = stepTransmittance.oneMinus();
				const contribution = transmittance.mul( scatteredFraction );
				const sampleDistance = sampleLocal.mul( worldUnitsPerLocalUnit );
				const intervalStartDistance = intervalStartLocal.mul( worldUnitsPerLocalUnit );
				const intervalEndDistance = intervalEndLocal.mul( worldUnitsPerLocalUnit );
				const previousAlpha = transmittance.oneMinus();
				const accumulatedAlpha = transmittance.mul( stepTransmittance ).oneMinus();
				const alphaInterval = max( accumulatedAlpha.sub( previousAlpha ), 0.00001 );

				If( frontFound.lessThan( 0.5 ).and( accumulatedAlpha.greaterThan( 0.02 ) ), () => {

					const crossing = float( 0.02 ).sub( previousAlpha ).div( alphaInterval ).clamp();
					frontDistance.assign( mix( intervalStartDistance, intervalEndDistance, crossing ) );
					frontFound.assign( 1 );

				} );

				If( medianFound.lessThan( 0.5 ).and( accumulatedAlpha.greaterThan( 0.5 ) ), () => {

					const crossing = float( 0.5 ).sub( previousAlpha ).div( alphaInterval ).clamp();
					medianDistance.assign( mix( intervalStartDistance, intervalEndDistance, crossing ) );
					medianFound.assign( 1 );

				} );

				radiance.addAssign( lighting.mul( contribution ) );
				meanDistance.addAssign( sampleDistance.mul( contribution ) );
				depthWeight.addAssign( contribution );
				transmittance.mulAssign( stepTransmittance );
				occupancy.addAssign( 1 );
				lightAccumulator.addAssign( lightTransmittance );

			} );

			If( transmittance.lessThan( uniforms.earlyExit ), () => {

				Break();

			} );

		} );

		const alpha = transmittance.oneMinus().clamp();

		If( depthWeight.greaterThan( 0.00001 ), () => {

			const representativeDistance = meanDistance.div( depthWeight );
			If( frontFound.lessThan( 0.5 ), () => frontDistance.assign( representativeDistance ) );
			If( medianFound.lessThan( 0.5 ), () => medianDistance.assign( representativeDistance ) );
			cloudDataOutput.assign( vec4( frontDistance, medianDistance, alpha, 1 ) );

		} );

		const outputColor = vec3( radiance ).toVar();

		If( uniforms.debugMode.greaterThan( 0.5 ), () => {

			outputColor.assign( vec3( alpha ) );

		} );

		If( uniforms.debugMode.greaterThan( 1.5 ), () => {

			outputColor.assign( vec3( transmittance ) );

		} );

		If( uniforms.debugMode.greaterThan( 2.5 ), () => {

			outputColor.assign( vec3( lightAccumulator.div( max( occupancy, 1 ) ) ) );

		} );

		return vec4( outputColor, alpha );

	} )();

	const material = new THREE.NodeMaterial();
	material.name = 'CyborgClouds ray-marched material';
	material.colorNode = fragmentNode;
	material.mrtNode = mrt( { cloudData: cloudDataOutput } );
	// Cloud radiance is an intermediate linear-HDR signal. The final pipeline
	// owns the single display tone-map and working-linear to sRGB conversion.
	material.toneMapped = false;
	material.side = THREE.BackSide;
	material.transparent = true;
	material.blending = THREE.CustomBlending;
	material.blendSrc = THREE.OneFactor;
	material.blendDst = THREE.OneMinusSrcAlphaFactor;
	material.blendSrcAlpha = THREE.OneFactor;
	material.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
	material.blending = THREE.CustomBlending;
	material.blendEquation = THREE.AddEquation;
	material.blendSrc = THREE.OneFactor;
	material.blendDst = THREE.OneMinusSrcAlphaFactor;
	material.blendSrcAlpha = THREE.OneFactor;
	material.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
	material.depthWrite = false;
	material.depthTest = true;

	return material;

}
